import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Colors from "../../../../Helper/ColorsConstant";


export default function WidgetCards({
    cardsData = [],
    minOpenTicketCount,
    autoRevertDurationinSec = 0,
    isDarkTheme = false,
    OpenTicketPlacement,
}) {
    const parentRef = useRef(null);
    const gridRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const cardRefs = useRef([]);
    const autoRevertTimerRef = useRef(null);
    const resizeDebounceRef = useRef(null);
    const lastCalcTimeRef = useRef(0);
    const initialStateRef = useRef(null);
    const isFirstCalculationRef = useRef(true);
    const calcRetryRef = useRef(0);
    const calcHandlerRef = useRef(null);
    const calcRunIdRef = useRef(0);
    const dataVersionRef = useRef(0);  // Track data version to prevent stale state restoration
    const isCalculatingRef = useRef(false);  // Track if calculation is in progress
    const appliedMeasureTokenRef = useRef(-1);
    const layoutMeasureTokenRef = useRef(0);
    const colors = Colors;
    const normalizedCardsData = useMemo(() => (Array.isArray(cardsData) ? cardsData : []), [cardsData]);
    const [widgetViewportHeight, setWidgetViewportHeight] = useState(0);
    const defaultHeightOfOpenCard = 160;
    //const defaultHeightOfTwoOpenCard = 150;
    const defaultHeightOfSingleCard = 160;
    const defaultHeightOfHeaderPx = 21;
    const defaultHeightOfAfter3HeaderPx = 25;



    const isOpenTicketPlacementTop = useMemo(
        () => String(OpenTicketPlacement || "bottom").toLowerCase() === "top",
        [OpenTicketPlacement]
    );
    const safeMinOpenCountForRender = useMemo(
        () => (Number.isFinite(Number(minOpenTicketCount)) ? Math.max(1, Math.min(5, Math.floor(Number(minOpenTicketCount)))) : 1),
        [minOpenTicketCount]
    );
    const estimatedTicketsToRender = useMemo(() => {
        const totalTickets = normalizedCardsData.length;
        if (totalTickets === 0) return 0;

        const minOpen = Math.min(safeMinOpenCountForRender, totalTickets);
        const containerPadPx = 2;
        const effectiveHeight = Math.max(0, widgetViewportHeight - (2 * containerPadPx));
        if (effectiveHeight <= 0) return minOpen;

        // First fit how many open tickets can realistically fit in the viewport.
        const estimatedCardSpacingPx = 4.2; // Average spacing based on measurements, can be refined further
        const estimateHeaderBlockPx = minOpen >= 3 ? defaultHeightOfAfter3HeaderPx : defaultHeightOfHeaderPx; // Approximate header height including padding
        console.log('estimateHeaderBlockPx', estimateHeaderBlockPx)
        const estimatedOpenHeaderBlockPx = estimateHeaderBlockPx;
        // const estimatedOpenBodyPerTicketPx = Math.max(
        //     150,
        //     Math.min(240, Math.floor(effectiveHeight * (minOpen >= 3 ? 0.28 : 0.3)))
        // );

        const estimatedOpenBodyPerTicketPx = minOpen === 1 ? defaultHeightOfSingleCard : defaultHeightOfOpenCard;

        const estimatedHighlyCollapsedHeaderBlockPx = 4;
        const estimatedMediumHeaderBlockPx = estimateHeaderBlockPx;
        const perCollapsedTicketPx = estimatedHighlyCollapsedHeaderBlockPx + estimatedCardSpacingPx;
        const perMediumCollapsedTicketPx = estimatedMediumHeaderBlockPx + estimatedCardSpacingPx;

        let fittedOpenCount = minOpen;
        while (fittedOpenCount > 0) {
            const openTicketsPx = fittedOpenCount * (estimatedOpenHeaderBlockPx + estimatedOpenBodyPerTicketPx);
            const openSpacingPx = Math.max(0, fittedOpenCount - 1) * estimatedCardSpacingPx;
            if ((openTicketsPx + openSpacingPx) <= effectiveHeight) {
                break;
            }
            fittedOpenCount -= 1;
        }

        const reservedOpenTicketsPx = fittedOpenCount * (estimatedOpenHeaderBlockPx + estimatedOpenBodyPerTicketPx);
        const reservedOpenSpacingPx = Math.max(0, fittedOpenCount - 1) * estimatedCardSpacingPx;
        let remainingHeightPx = Math.max(0, effectiveHeight - (reservedOpenTicketsPx + reservedOpenSpacingPx));



        const baseTickets = fittedOpenCount;//+ mediumForOpenDeficit + collapsedForOpenDeficit;
        // Use existing balanced mix for any additional tickets after open/deficit allocation.
        const mixWeightMedium = 0.33;
        const mediumHeightBudget = remainingHeightPx * mixWeightMedium;
        const collapsedHeightBudget = Math.max(0, remainingHeightPx - mediumHeightBudget);
        const mediumCollapsedTickets = Math.max(0, Math.floor(mediumHeightBudget / perMediumCollapsedTicketPx));
        const additionalCollapsedTickets = Math.max(0, Math.floor(collapsedHeightBudget / perCollapsedTicketPx));

        return Math.max(
            baseTickets,
            Math.min(totalTickets, baseTickets + additionalCollapsedTickets + mediumCollapsedTickets)
        );
    }, [normalizedCardsData.length, safeMinOpenCountForRender, widgetViewportHeight]);

    console.log("Estimated tickets to render based on viewport:", estimatedTicketsToRender, "for viewport height:", widgetViewportHeight);

    const [maxTicketsToRender, setMaxTicketsToRender] = useState(estimatedTicketsToRender);

    useEffect(() => {
        setMaxTicketsToRender((prev) => {
            const boundedEstimate = Math.max(
                safeMinOpenCountForRender,
                Math.min(normalizedCardsData.length, estimatedTicketsToRender)
            );
            console.log("Adjusting maxTicketsToRender from", prev, "to", boundedEstimate, "based on new estimate:", estimatedTicketsToRender);
            if (boundedEstimate === prev) return prev;
            console.log("Updated maxTicketsToRender to:", boundedEstimate);
            return boundedEstimate;
        });
    }, [estimatedTicketsToRender, normalizedCardsData.length, safeMinOpenCountForRender]);

    const displayCardsData = useMemo(
        () => {
            console.log("Rendering with maxTicketsToRender:", maxTicketsToRender, "out of", normalizedCardsData.length, normalizedCardsData.slice(-maxTicketsToRender));
            return normalizedCardsData.slice(-maxTicketsToRender)
        },
        [normalizedCardsData, maxTicketsToRender]
    );
    const themeStyles = useMemo(() => ({
        cardBackground: isDarkTheme ? "rgb(38, 38, 38)" : "#ffffff",
        cardBorder: isDarkTheme ? "1.5px solid rgb(63, 63, 63)" : "1.5px solid rgba(0,0,0,.125)",
        bodyTextColor: isDarkTheme ? "#b5b5b5" : "inherit",
        mutedTextColor: isDarkTheme ? "#b5b5b5" : "#6c757d",
    }), [isDarkTheme]);

    const [cardStates, setCardStates] = useState(
        displayCardsData.map((_, index) => ({
            isExpanded: index === 0,
            isHalfOpen: false,
            fullHeight: 0,  // Store full height for accurate half-open calculation
            visibleBodyHeight: 0,
            ExtraMargin: 0,
            renderMode: index === 0 ? "open" : "collapsed",
            priorCompressedMode: "collapsed",
        }))
    );
    const [isReady, setIsReady] = useState(false);  // Hide cards until first calc completes
    const [disableBodyTransition, setDisableBodyTransition] = useState(true);
    const hasInitiallyLoadedRef = useRef(false);  // Track if we've loaded at least once
    const isMeasuringRef = useRef(false);  // Track if we're in the measurement phase specifically
    const [imageErrors, setImageErrors] = useState({});  // Track image load errors by card id
    const [compactStatusStyle, setCompactStatusStyle] = useState({
        headerPadPx: 2,
        headerMinHeightPx: 10,
        statusPadPx: 2,
        headerFontRem: 0.75,
        statusLabelRem: 0.8,
        statusValueRem: 1,
        bodyPaddingPx: 8,
        avatarSizePx: 50,
        avatarFontPx: 18,
        ownerFontRem: 0.85,
        topLeftFontRem: 0.85,
        titleFontRem: 1,
        descFontRem: 0.85,
        metaFontRem: 0.75,
        titleLines: 1,
        descLines: 2,
        containerPadPx: 3,
        avatarWrapWidthPx: 80,
        showHeaderText: true,
    });

    useLayoutEffect(() => {
        if (!parentRef.current) return;

        const updateViewportHeight = () => {
            const rawHeight = scrollContainerRef.current?.clientHeight || parentRef.current?.clientHeight || 0;
            // Quantize height to dampen 1-2px observer jitter that can cause re-layout flicker.
            const quantizedHeight = Math.max(0, Math.round(rawHeight / 8) * 8);
            setWidgetViewportHeight((prev) => (prev === quantizedHeight ? prev : quantizedHeight));
        };

        updateViewportHeight();

        if (typeof ResizeObserver !== "undefined") {
            const observer = new ResizeObserver(() => updateViewportHeight());
            observer.observe(parentRef.current);
            return () => observer.disconnect();
        }

        window.addEventListener("resize", updateViewportHeight);
        return () => window.removeEventListener("resize", updateViewportHeight);
    }, []);

    useLayoutEffect(() => {
        // Increment data version to invalidate any pending auto-reverts
        dataVersionRef.current += 1;

        initialStateRef.current = null;
        isFirstCalculationRef.current = true;
        calcRetryRef.current = 0;
        calcRunIdRef.current += 1;
        cardRefs.current = new Array(displayCardsData.length);

        // Clear any pending auto-revert timer when data changes
        if (autoRevertTimerRef.current) {
            clearTimeout(autoRevertTimerRef.current);
            autoRevertTimerRef.current = null;
        }

        // Reset image errors when data changes
        setImageErrors({});

        if (displayCardsData.length === 0) {
            setCardStates([]);
            setIsReady(true);
            setDisableBodyTransition(false);
            hasInitiallyLoadedRef.current = true;
            return;
        }

        // Deterministic refresh: always reset and recalculate to avoid stale layout/state
        isCalculatingRef.current = true;
        if (!hasInitiallyLoadedRef.current) {
            setIsReady(false);
        }
        setDisableBodyTransition(true);
        setCardStates(
            displayCardsData.map((_, index) => ({
                isExpanded: false,
                isHalfOpen: false,
                fullHeight: 0,
                visibleBodyHeight: 0,
                ExtraMargin: 0,
                renderMode: "collapsed",
                priorCompressedMode: "collapsed",
            }))
        );

        const scheduleCalc = (delayMs = 0) => {
            const run = () => {
                const doCalc = () => requestAnimationFrame(() => requestAnimationFrame(() => calcHandlerRef.current?.()));
                if (document.fonts && document.fonts.ready) {
                    document.fonts.ready.then(doCalc).catch(doCalc);
                } else {
                    doCalc();
                }
            };
            if (delayMs > 0) return setTimeout(run, delayMs);
            run();
            return null;
        };

        const timer = scheduleCalc(0);

        return () => {
            if (timer) clearTimeout(timer);
            if (autoRevertTimerRef.current) {
                clearTimeout(autoRevertTimerRef.current);
                autoRevertTimerRef.current = null;
            }
        };
    }, [displayCardsData, minOpenTicketCount, isOpenTicketPlacementTop]);

    useEffect(() => {
        // Recalculate after images and fonts load to stabilize first render vs refresh
        const triggerCalc = () => {
            const run = () => requestAnimationFrame(() => requestAnimationFrame(() => calcHandlerRef.current?.()));
            if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(run).catch(run);
            } else {
                run();
            }
        };

        const timer = setTimeout(() => {
            if (!hasInitiallyLoadedRef.current) {
                triggerCalc();
            }
        }, 300);

        const images = parentRef.current
            ? parentRef.current.querySelectorAll(".card img")
            : [];
        const handleImageLoad = () => triggerCalc();

        images.forEach((img) => {
            if (!img.complete) {
                img.addEventListener("load", handleImageLoad);
                img.addEventListener("error", handleImageLoad);
            }
        });

        return () => {
            clearTimeout(timer);
            images.forEach((img) => {
                img.removeEventListener("load", handleImageLoad);
                img.removeEventListener("error", handleImageLoad);
            });
        };
    }, [displayCardsData]);

    useEffect(() => {
        // Simple window resize handler with debounce
        const handleResize = () => {
            if (resizeDebounceRef.current) {
                clearTimeout(resizeDebounceRef.current);
            }
            resizeDebounceRef.current = setTimeout(() => {
                calcHandlerRef.current?.();
                resizeDebounceRef.current = null;
            }, 300);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            if (resizeDebounceRef.current) {
                clearTimeout(resizeDebounceRef.current);
            }
            window.removeEventListener("resize", handleResize);
            // Clean up auto-revert timer on unmount
            if (autoRevertTimerRef.current) {
                clearTimeout(autoRevertTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        calcHandlerRef.current = calculateAndAdjustHeight;
    });

    useLayoutEffect(() => {
        if (!isReady || displayCardsData.length === 0) return;

        const token = layoutMeasureTokenRef.current;
        if (token === appliedMeasureTokenRef.current) return;

        let raf1 = 0;
        let raf2 = 0;
        let settleTimer = null;
        let measureRetryCount = 0;

        const measureAndApply = () => {
            const gridEl = gridRef.current;
            const viewportEl = scrollContainerRef.current || parentRef.current;
            if (!gridEl || !viewportEl) {
                appliedMeasureTokenRef.current = token;
                return;
            }

            if (isCalculatingRef.current) {
                if (measureRetryCount < 30) {
                    measureRetryCount += 1;
                    settleTimer = setTimeout(measureAndApply, 50);
                }
                return;
            }

            const domCardElements = Array.from(gridEl.querySelectorAll(":scope > .card"));
            const cardElements = (domCardElements.length > 0
                ? domCardElements
                : cardRefs.current.filter(Boolean)
            ).slice(0, displayCardsData.length);

            if (cardElements.length === 0) {
                appliedMeasureTokenRef.current = token;
                return;
            }

            // Measuring a partial set of cards on first load produces a false large gap.
            if (cardElements.length < displayCardsData.length && measureRetryCount < 30) {
                measureRetryCount += 1;
                settleTimer = setTimeout(measureAndApply, 50);
                return;
            }

            if (cardElements.length < displayCardsData.length) {
                console.log("Skipping trailing height measurement until all cards mount:", cardElements.length, "/", displayCardsData.length);
                return;
            }

            const viewportStyles = window.getComputedStyle(viewportEl);
            const viewportPaddingTop = parseFloat(viewportStyles.paddingTop || "0") || 0;
            const viewportPaddingBottom = parseFloat(viewportStyles.paddingBottom || "0") || 0;
            const viewportInnerHeight = Math.max(0, viewportEl.clientHeight - viewportPaddingTop - viewportPaddingBottom);

            const gridStyles = window.getComputedStyle(gridEl);
            const gridMarginBottom = parseFloat(gridStyles.marginBottom || "0") || 0;
            const contentHeight = Math.max(
                gridEl.scrollHeight,
                Math.ceil(gridEl.getBoundingClientRect().height + gridMarginBottom)
            );

            const signedTrailingDelta = Number((viewportInnerHeight - contentHeight).toFixed(3));
            console.log("Measured trailing empty height delta:", signedTrailingDelta, "px");

            appliedMeasureTokenRef.current = token;

            if (signedTrailingDelta <= 0.5) return;
            console.log("Applying trailing delta adjustment of", signedTrailingDelta, "px");

            setCardStates((prevStates) => {
                const openIndexes = [];
                prevStates.forEach((state, idx) => {
                    if (state.isExpanded) openIndexes.push(idx);
                });

                if (openIndexes.length === 0) return prevStates;

                const openIndexSet = new Set(openIndexes);
                const extraPerOpenCard = (signedTrailingDelta - (openIndexes.length * 6)) / openIndexes.length;
                console.log("extraPerOpenCard", extraPerOpenCard, "px");

                return prevStates.map((state, idx) => {
                    if (!openIndexSet.has(idx)) return state;
                    return {
                        ...state,
                        visibleBodyHeight: Math.max(0, Number(state.visibleBodyHeight || 0)) + extraPerOpenCard,
                    };
                });
            });
        };

        const settleDelayMs = disableBodyTransition ? 150 : 260;
        raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => {
                if (settleDelayMs > 0) {
                    settleTimer = setTimeout(measureAndApply, settleDelayMs);
                } else {
                    measureAndApply();
                }
            });
        });

        return () => {
            if (raf1) cancelAnimationFrame(raf1);
            if (raf2) cancelAnimationFrame(raf2);
            if (settleTimer) clearTimeout(settleTimer);
        };
    }, [cardStates, isReady, displayCardsData.length, disableBodyTransition]);


    const calculateAndAdjustHeight = () => {
        if (displayCardsData.length === 0) {
            setIsReady(true);
            setDisableBodyTransition(false);
            return;
        }

        // Allow first calculation to always run. During initial load, allow extra
        // stabilization passes (fonts/images/observer) without throttle blocking.
        const now = Date.now();
        const isInitialLoadStabilizing = !hasInitiallyLoadedRef.current;
        if (!isFirstCalculationRef.current && !isInitialLoadStabilizing && now - lastCalcTimeRef.current < 400) return;

        if (!parentRef.current || cardRefs.current.length === 0) {
            if (calcRetryRef.current < 5) {
                calcRetryRef.current += 1;
                setTimeout(calculateAndAdjustHeight, 100);
            }
            return;
        }

        isFirstCalculationRef.current = false; // Mark first calculation as done
        lastCalcTimeRef.current = now;

        const parentHeight = parentRef.current.clientHeight;
        const cardElements = cardRefs.current.slice(0, displayCardsData.length);

        // Calculate layout using the same viewport height source as estimatedTicketsToRender.
        const containerHeight = Math.max(
            0,
            widgetViewportHeight || scrollContainerRef.current?.clientHeight || parentHeight
        );

        const primaryOpenBodyPx = minOpenTicketCount === 1 ? defaultHeightOfSingleCard : defaultHeightOfOpenCard;

        if (cardElements.length === 0 || cardElements.some((el) => !el)) {
            if (calcRetryRef.current < 5) {
                calcRetryRef.current += 1;
                setTimeout(calculateAndAdjustHeight, 100);
            }
            return;
        }

        isCalculatingRef.current = true;
        isMeasuringRef.current = true;
        setDisableBodyTransition(true);



        // Wait a tick for layout to recalculate
        const currentCalcRunId = ++calcRunIdRef.current;
        requestAnimationFrame(() => {
            const restoreMeasuredBodies = () => {
                // originalHeights.forEach((item) => {
                //     if (item) {
                //         item.body.style.maxHeight = item.original.maxHeight;
                //         item.body.style.display = item.original.display;
                //         item.body.style.overflow = item.original.overflow;
                //         item.body.style.padding = item.original.padding;
                //         item.body.style.margin = item.original.margin;
                //         item.body.style.visibility = item.original.visibility;
                //         item.body.style.position = item.original.position;
                //         item.body.style.left = item.original.left;
                //         item.body.style.right = item.original.right;
                //         item.body.style.width = item.original.width;
                //         item.body.style.pointerEvents = item.original.pointerEvents;
                //     }
                // });
            };

            if (currentCalcRunId !== calcRunIdRef.current) {
                restoreMeasuredBodies();
                isMeasuringRef.current = false;
                setDisableBodyTransition(false);
                return;
            }

            const cardSpacing = cardElements.map((card) => {
                const styles = window.getComputedStyle(card);
                const marginBottom = parseFloat(styles.marginBottom || "0");
                return Number.isFinite(marginBottom) ? marginBottom : 0;
            });
            // Measure all card body heights
            const cardHeights = cardElements.map((card) => {
                const body = card.querySelector(".card-body");
                const measuredBodyHeight = body ? body.scrollHeight : 0;
                return Math.max(primaryOpenBodyPx, Math.floor(measuredBodyHeight || 0));
            });

            // Restore original display state
            restoreMeasuredBodies();

            // Measurement phase is complete, allow transitions again for the state updates
            isMeasuringRef.current = false;
            setDisableBodyTransition(false);


            // Dynamically compress header/status area based on available height per card
            const containerPadPx = 2;
            const availableHeight = containerHeight - (containerPadPx * 2);
            const perCardHeight = availableHeight / Math.max(cardElements.length, 1);
            const compressionRatio = Math.max(0, Math.min(1, (90 - perCardHeight) / 60));
            const roomyRatio = 1 - compressionRatio;

            const headerPadPx = 2; //Math.max(0, Math.round(4 * roomyRatio));
            const headerMinHeightPx = Math.max(2, Math.round(3 + (11 * roomyRatio)));
            const statusPadPx = Math.max(0, Math.round(4 * roomyRatio));
            const headerFontRem = Number((0.56 + (0.26 * roomyRatio)).toFixed(2));

            // Status text - keep standard sizes for clear readability
            const statusLabelRem = 0.8;
            const statusValueRem = 1;

            // Body content - keep standard sizes for clear readability
            const bodyPaddingPx = 8;
            const avatarSizePx = 50;
            const avatarFontPx = 18;
            const ownerFontRem = 0.85;
            const topLeftFontRem = 0.85;
            const titleFontRem = 1;
            const descFontRem = 0.85;
            const metaFontRem = 0.75;
            const titleLines = 2;
            const descLines = 2;

            const avatarWrapWidthPx = 80;
            const showHeaderText = true;
            //const minPrimaryFitPx = Math.max(40, Math.floor(availableHeight * 0.20));
            const safeMinOpenCount = Number.isFinite(Number(minOpenTicketCount))
                ? Math.max(1, Math.min(5, Math.floor(Number(minOpenTicketCount))))
                : 1;

            const newCardStates = displayCardsData.map((_, index) => ({
                isExpanded: false,
                isHalfOpen: false,
                fullHeight: cardHeights[index] || 0,  // Store full height
                visibleBodyHeight: 0,
                ExtraMargin: 0,
                renderMode: "collapsed",
                priorCompressedMode: "collapsed",
            }));


            const totalSpacing = cardSpacing.slice(0, -1).reduce((sum, spacing) => sum + spacing, 0);

            setCompactStatusStyle((prev) => {
                if (
                    prev.headerPadPx === headerPadPx &&
                    prev.headerMinHeightPx === headerMinHeightPx &&
                    prev.statusPadPx === statusPadPx &&
                    prev.headerFontRem === headerFontRem &&
                    prev.statusLabelRem === statusLabelRem &&
                    prev.statusValueRem === statusValueRem &&
                    prev.bodyPaddingPx === bodyPaddingPx &&
                    prev.avatarSizePx === avatarSizePx &&
                    prev.avatarFontPx === avatarFontPx &&
                    prev.ownerFontRem === ownerFontRem &&
                    prev.topLeftFontRem === topLeftFontRem &&
                    prev.titleFontRem === titleFontRem &&
                    prev.descFontRem === descFontRem &&
                    prev.metaFontRem === metaFontRem &&
                    prev.titleLines === titleLines &&
                    prev.descLines === descLines &&
                    prev.containerPadPx === containerPadPx &&
                    prev.avatarWrapWidthPx === avatarWrapWidthPx &&
                    prev.showHeaderText === showHeaderText
                ) {
                    return prev;
                }
                return {
                    headerPadPx,
                    headerMinHeightPx,
                    statusPadPx,
                    headerFontRem,
                    statusLabelRem,
                    statusValueRem,
                    bodyPaddingPx,
                    avatarSizePx,
                    avatarFontPx,
                    ownerFontRem,
                    topLeftFontRem,
                    titleFontRem,
                    descFontRem,
                    metaFontRem,
                    titleLines,
                    descLines,
                    containerPadPx,
                    avatarWrapWidthPx,
                    showHeaderText,
                };
            });

            const normalHeaderPadPxForCalc = 0; //Math.max(2, headerPadPx + 1);
            const normalHeaderMinHeightPxForCalc = minOpenTicketCount >= 3 ? defaultHeightOfAfter3HeaderPx : defaultHeightOfHeaderPx; //Math.max(12, headerMinHeightPx + 2);
            const mediumHeaderPadPxForCalc = normalHeaderPadPxForCalc; //Math.max(1, normalHeaderPadPxForCalc - 1);
            const mediumHeaderMinHeightPxForCalc = normalHeaderMinHeightPxForCalc; //Math.max(8, normalHeaderMinHeightPxForCalc - 3);
            const collapsedHeaderPadPxForCalc = 0; //Math.max(0, mediumHeaderPadPxForCalc - 2);
            //const collapsedHeaderMinHeightPxForCalc = Math.max(2, mediumHeaderMinHeightPxForCalc - 5);
            const collapsedHeaderMinHeightPxForCalc = 4; //Math.max(2, mediumHeaderMinHeightPxForCalc - 8);

            const normalHeaderBlockPxForCalc = normalHeaderMinHeightPxForCalc + (normalHeaderPadPxForCalc * 2);
            const mediumHeaderBlockPxForCalc = mediumHeaderMinHeightPxForCalc + (mediumHeaderPadPxForCalc * 2);
            const collapsedHeaderBlockPxForCalc = collapsedHeaderMinHeightPxForCalc + (collapsedHeaderPadPxForCalc * 2);

            // Baseline all cards as highly-compressed first, then upgrade selected cards to medium/open.
            const compressedHeaderHeight = collapsedHeaderBlockPxForCalc;
            const collapsedBaselineHeight = cardElements.length * compressedHeaderHeight + totalSpacing;
            let availableBodyHeight = Math.max(0, availableHeight - collapsedBaselineHeight);
            //let availableBodyHeight = Math.max(0, availableHeight);

            // Height-budget based 3-tier grouping:
            // 1) Open tickets (normal) => maximize count first
            // 2) Medium compressed => consume remaining body budget
            // 3) Highly compressed => header-only collapsed
            const fullOpenFallbackPx = defaultHeightOfOpenCard; //Math.max(minPrimaryFitPx, Math.floor(availableHeight * 0.22));
            //const openHeaderUpgradeCostPx = Math.max(0, normalHeaderBlockPxForCalc - collapsedHeaderBlockPxForCalc);

            const buildOpenIndexes = (count) => (count > 0
                ? (isOpenTicketPlacementTop
                    ? Array.from({ length: count }, (_, idx) => idx)
                    : Array.from({ length: count }, (_, idx) => cardElements.length - count + idx))
                : []);

            const targetOpenCount = Math.min(safeMinOpenCount, cardElements.length);
            let fittedOpenCount = targetOpenCount;
            while (fittedOpenCount > 0) {
                const candidateIndexes = buildOpenIndexes(fittedOpenCount);
                const candidateOpenHeights = candidateIndexes.reduce((sum, idx) => {
                    const fullHeight = Math.max(1, Math.floor(cardHeights[idx] || fullOpenFallbackPx));
                    return sum + fullHeight;
                }, 0);
                const candidateHeaderCost = candidateIndexes.length * normalHeaderBlockPxForCalc;
                if ((candidateOpenHeights + candidateHeaderCost) <= availableBodyHeight) {
                    break;
                }
                fittedOpenCount -= 1;
            }

            const openIndexes = buildOpenIndexes(fittedOpenCount);
            const openIndexSet = new Set(openIndexes);
            const openCardsMeta = openIndexes.map((idx) => ({
                idx,
                fullHeight: Math.max(1, Math.floor(cardHeights[idx] || fullOpenFallbackPx)),
            }));

            const applyOpenTicketLayout = (openBodyBudgetPx) => {
                const safeBudget = Math.max(0, Math.floor(openBodyBudgetPx));
                const openCount = openCardsMeta.length;
                if (openCount === 0 || safeBudget <= 0) return 0;

                const allocatedBodies = openCardsMeta.map((item) => item.fullHeight);
                const requiredBudget = allocatedBodies.reduce((sum, value) => sum + value, 0);
                if (safeBudget < requiredBudget) return 0;

                openCardsMeta.forEach((item, orderIdx) => {
                    const visibleBodyHeight = Math.max(1, Math.floor(allocatedBodies[orderIdx]));
                    newCardStates[item.idx].isExpanded = true;
                    newCardStates[item.idx].isHalfOpen = false;
                    newCardStates[item.idx].visibleBodyHeight = visibleBodyHeight;
                    newCardStates[item.idx].renderMode = "open";
                });

                return Math.min(
                    safeBudget,
                    openCardsMeta.reduce((sum, _, idx) => sum + Math.max(1, Math.floor(allocatedBodies[idx])), 0)
                );
            };

            if (openIndexes.length > 0) {
                const openHeaderBudgetPx = openIndexes.length * normalHeaderBlockPxForCalc;
                const openBodyBudgetPx = Math.max(0, availableBodyHeight - openHeaderBudgetPx);

                const consumedOpenBodyPx = applyOpenTicketLayout(openBodyBudgetPx);
                const consumedOpenBudgetPx = openHeaderBudgetPx + consumedOpenBodyPx;
                availableBodyHeight = Math.max(0, availableBodyHeight - consumedOpenBudgetPx);
            }

            const remainingIndexes = [];
            for (let idx = 0; idx < cardElements.length; idx++) {
                if (!openIndexSet.has(idx)) {
                    remainingIndexes.push(idx);
                }
            }

            const applyCompressedModes = (indexes, remainingBodyHeightPx) => {
                const allocationOrder = isOpenTicketPlacementTop
                    ? indexes
                    : [...indexes].reverse();
                const nonOpenCount = indexes.length;
                const dynamicSpaceForCompressedModes = Math.max(0, remainingBodyHeightPx);
                const mediumUpgradePx = Math.max(1, mediumHeaderBlockPxForCalc - collapsedHeaderBlockPxForCalc);
                const maxMediumByBudget = Math.min(nonOpenCount, Math.floor(dynamicSpaceForCompressedModes / mediumUpgradePx));

                const mediumCount = maxMediumByBudget; //Math.min(maxMediumByBudget, guaranteedMedium + additionalMediumByRatio);
                const consumedBudgetPx = mediumCount * mediumUpgradePx;

                allocationOrder.forEach((idx, orderIdx) => {
                    const shouldBeMedium = orderIdx < mediumCount;

                    newCardStates[idx].isExpanded = false;
                    newCardStates[idx].isHalfOpen = false;
                    newCardStates[idx].visibleBodyHeight = 0;
                    newCardStates[idx].renderMode = shouldBeMedium ? "medium" : "collapsed";
                    newCardStates[idx].priorCompressedMode = shouldBeMedium ? "medium" : "collapsed";
                });

                return Math.max(0, dynamicSpaceForCompressedModes - consumedBudgetPx);
            };

            applyCompressedModes(remainingIndexes, availableBodyHeight);


            // Mark calculation as complete BEFORE setting states
            isCalculatingRef.current = false;
            // (disableBodyTransition is already set to false after measurement phase)

            if (currentCalcRunId !== calcRunIdRef.current) return;

            layoutMeasureTokenRef.current += 1;
            setCardStates(newCardStates);
            calcRetryRef.current = 0;

            // Store the initial calculated state for auto-revert with data version
            if (!initialStateRef.current) {
                initialStateRef.current = {
                    states: JSON.parse(JSON.stringify(newCardStates)),  // Deep copy
                    dataVersion: dataVersionRef.current
                };
            }

            // Always set ready after calculation completes
            setIsReady(true);
            hasInitiallyLoadedRef.current = true;
        });
    };

    const toggleCard = (index) => {
        // Enable transitions immediately when user toggles a card
        // This ensures animations play for user interactions even during calculation
        setDisableBodyTransition(false);
        layoutMeasureTokenRef.current += 1;

        setCardStates((prevStates) => {
            const newStates = [...prevStates];
            const currentlyExpanded = newStates[index].isExpanded;
            const isCurrentlyClosed = !currentlyExpanded;
            const getCompressedMode = (state) => (state?.priorCompressedMode === "medium" ? "medium" : "collapsed");
            const mediumCardCount = newStates.filter((state, stateIndex) => stateIndex !== index && !state.isExpanded && state.renderMode === "medium").length;

            console.log('mediumCardCount', mediumCardCount);

            const assignMediumCardsAroundOpen = (openIndex, requestedMediumCount) => {
                if (requestedMediumCount <= 0) return;

                const candidateIndexesAbove = [];
                const candidateIndexesBelow = [];

                for (let i = openIndex - 1; i >= 0; i--) {
                    if (!newStates[i]?.isExpanded) {
                        candidateIndexesAbove.push(i);
                    }
                }

                for (let i = openIndex + 1; i < newStates.length; i++) {
                    if (!newStates[i]?.isExpanded) {
                        candidateIndexesBelow.push(i);
                    }
                }

                const selectedIndexes = candidateIndexesAbove.slice(0, requestedMediumCount);
                if (selectedIndexes.length < requestedMediumCount) {
                    selectedIndexes.push(...candidateIndexesBelow.slice(0, requestedMediumCount - selectedIndexes.length));
                }

                selectedIndexes.forEach((selectedIndex) => {
                    newStates[selectedIndex].renderMode = "medium";
                    newStates[selectedIndex].priorCompressedMode = "medium";
                });
            };

            for (let i = 0; i < newStates.length; i++) {
                if (!newStates[i].isExpanded && newStates[i].renderMode === "medium") {
                    newStates[i].renderMode = "collapsed";
                    newStates[i].priorCompressedMode = "collapsed";
                }
            }

            const openNextTicket = (closedIndex) => {
                if (!Array.isArray(newStates) || newStates.length <= 1) return;

                const length = newStates.length;
                const preferredStep = isOpenTicketPlacementTop ? 1 : -1;

                const tryOpenCandidate = (candidateIndex) => {
                    const candidateState = newStates[candidateIndex];
                    if (!candidateState || candidateState.isExpanded) return false;

                    candidateState.priorCompressedMode = getCompressedMode(candidateState);
                    candidateState.isExpanded = true;
                    candidateState.isHalfOpen = false;
                    candidateState.visibleBodyHeight = candidateState.fullHeight || 0;
                    candidateState.renderMode = "open";
                    return candidateIndex;
                };

                const nextIndex = (closedIndex + preferredStep + length) % length;
                const previousIndex = (closedIndex - preferredStep + length) % length;

                // Only consider immediate neighbor first, then the opposite neighbor.
                const nextOpenedIndex = tryOpenCandidate(nextIndex);
                if (nextOpenedIndex !== false) {
                    return nextOpenedIndex;
                }

                return tryOpenCandidate(previousIndex);
            };

            const safeMinOpenCount = Number.isFinite(Number(minOpenTicketCount))
                ? Math.max(1, Math.min(5, Math.floor(Number(minOpenTicketCount))))
                : 1;

            // If opening a closed card, check if we need to close another to maintain space
            if (isCurrentlyClosed) {
                // Requirement: when opening a ticket, close any other fully expanded ticket first.
                const closeStart = isOpenTicketPlacementTop ? newStates.length - 1 : 0;
                const closeEnd = isOpenTicketPlacementTop ? -1 : newStates.length;
                const closeStep = isOpenTicketPlacementTop ? -1 : 1;
                let fullyExpandedClosed = false;

                for (let i = closeStart; i !== closeEnd; i += closeStep) {
                    if (i !== index && newStates[i].isExpanded) {
                        const restoredMode = getCompressedMode(newStates[i]);
                        newStates[i].isExpanded = false;
                        newStates[i].isHalfOpen = false;
                        newStates[i].visibleBodyHeight = 0;
                        newStates[i].renderMode = restoredMode;
                        fullyExpandedClosed = true;
                        break;
                    }
                }

                // Count currently open cards
                const openCount = newStates.filter(s => s.isExpanded).length;

                // If still at or over the configured limit, close one more open card.
                if (!fullyExpandedClosed && openCount >= safeMinOpenCount) {
                    for (let i = closeStart; i !== closeEnd; i += closeStep) {
                        if (i !== index && newStates[i].isExpanded) {
                            const restoredMode = getCompressedMode(newStates[i]);
                            newStates[i].isExpanded = false;
                            newStates[i].isHalfOpen = false;
                            newStates[i].visibleBodyHeight = 0;
                            newStates[i].renderMode = restoredMode;
                            break;
                        }
                    }
                }

                newStates[index].priorCompressedMode = getCompressedMode(newStates[index]);
                // Now open the clicked card as fully expanded
                newStates[index].isExpanded = true;
                newStates[index].isHalfOpen = false;
                newStates[index].visibleBodyHeight = newStates[index].fullHeight || 0;
                newStates[index].renderMode = "open";
                assignMediumCardsAroundOpen(index, mediumCardCount);
            } else {
                // Fully expanded - collapse it completely
                const restoredMode = getCompressedMode(newStates[index]);
                newStates[index].isExpanded = false;
                newStates[index].isHalfOpen = false;
                newStates[index].visibleBodyHeight = 0;
                newStates[index].renderMode = restoredMode;

                // When closing an open ticket, automatically open the next ticket
                const autoOpenedIndex = openNextTicket(index);
                if (autoOpenedIndex !== false && autoOpenedIndex !== undefined) {
                    assignMediumCardsAroundOpen(autoOpenedIndex, mediumCardCount);
                }
            }

            return newStates.map((state) => {
                if (!state?.isExpanded) {
                    return {
                        ...state,
                        visibleBodyHeight: 0,
                    };
                }

                return {
                    ...state,
                    visibleBodyHeight: Math.max(1, Number(state.fullHeight || state.visibleBodyHeight || 0)),
                };
            });
        });

        // Clear any existing timer
        if (autoRevertTimerRef.current) {
            clearTimeout(autoRevertTimerRef.current);
        }

        // Set new timer to revert if autoRevertDurationinSec is greater than 0
        if (autoRevertDurationinSec > 0) {
            const currentVersion = dataVersionRef.current;
            autoRevertTimerRef.current = setTimeout(() => {
                // Only restore if data hasn't changed
                if (initialStateRef.current &&
                    initialStateRef.current.dataVersion === currentVersion &&
                    initialStateRef.current.states.length === cardStates.length) {
                    const restoredState = JSON.parse(JSON.stringify(initialStateRef.current.states));  // Deep copy
                    setDisableBodyTransition(false);
                    layoutMeasureTokenRef.current += 1;
                    setCardStates(restoredState);
                }
                autoRevertTimerRef.current = null;
            }, autoRevertDurationinSec * 1000);
        }
    };

    const formatOwnerName = (name) => {
        if (!name) return "";
        const words = name.trim().split(/\s+/);
        if (words.length === 1) return words[0];

        // First word as is, rest as initials
        const formatted = [words[0]];
        for (let i = 1; i < words.length; i++) {
            formatted.push(words[i][0].toUpperCase());
        }
        return formatted.join(" ");
    };

    const getInitials = (name) => {
        if (!name) return "?";
        const words = name.trim().split(/\s+/);
        if (words.length === 1) return words[0][0]?.toUpperCase() || "?";
        // First letter of first word + first letter of last word
        return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    };

    const handleImageError = (cardId) => {
        setImageErrors((prev) => ({ ...prev, [cardId]: true }));
    };

    const shouldShowCards = isReady || hasInitiallyLoadedRef.current;

    return (
        <div ref={parentRef} style={{ height: "100%", width: "100%" }}>
            <div
                className="slim-scroll"
                ref={scrollContainerRef}
                style={{
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                    opacity: shouldShowCards ? 1 : 0,
                    visibility: shouldShowCards ? "visible" : "hidden",
                    transition: "none",
                }}
            >
                <div ref={gridRef} style={{ padding: `${compactStatusStyle.containerPadPx}px` }}>
                    {displayCardsData.map((card, index) => (
                        (() => {
                            const cardState = cardStates[index] || {};
                            const isExpanded = Boolean(cardState.isExpanded);
                            const renderMode = (cardState.renderMode === "open" || isExpanded)
                                ? "open"
                                : (cardState.renderMode === "medium" ? "medium" : "collapsed");
                            const isCollapsedMode = renderMode === "collapsed";
                            const isMediumMode = renderMode === "medium";
                            const isBodyOpen = renderMode === "open";
                            const fullHeight = Number(cardState.fullHeight || 0);
                            const visibleBodyHeight = Number(cardState.visibleBodyHeight || 0);
                            const computedVisibleHeight = renderMode === "open"
                                ? Math.max(1, visibleBodyHeight || fullHeight)
                                : 0;
                            const bodyHeight = renderMode === "open"
                                ? `${Math.max(0, computedVisibleHeight).toFixed(3)}px`
                                : "0px";
                            const bodyDisplay = renderMode === "open" ? "flex" : "block";
                            const bodyJustifyContent = "flex-start";
                            const bodyPadding = renderMode === "open" ? `${compactStatusStyle.bodyPaddingPx}px` : "0";
                            const bodyMargin = renderMode === "open" ? "" : "0";
                            const cardTransform = isReady ? "translateY(0px)" : "translateY(4px)";
                            const contentOpacity = isBodyOpen ? 1 : 0;
                            const contentTransition = disableBodyTransition
                                ? "none"
                                : "opacity 420ms cubic-bezier(0.22, 1, 0.36, 1)";
                            const normalHeaderPadPx = 2; // Math.max(2, compactStatusStyle.headerPadPx + 1);
                            const normalHeaderMinHeightPx = 12; //Math.max(12, compactStatusStyle.headerMinHeightPx + 2);
                            const normalHeaderFontRem = 0.76; //Math.max(0.76, compactStatusStyle.headerFontRem + 0.08);

                            const mediumHeaderPadPx = normalHeaderPadPx; //Math.max(1, normalHeaderPadPx - 1);
                            const mediumHeaderMinHeightPx = normalHeaderMinHeightPx; //Math.max(8, normalHeaderMinHeightPx - 3);
                            const mediumHeaderFontRem = normalHeaderFontRem; //Math.max(0.64, Number((normalHeaderFontRem - 0.1).toFixed(2)));

                            const collapsedHeaderPadPx = 0; //Math.max(0, mediumHeaderPadPx - 2);
                            const collapsedHeaderMinHeightPx = 2;// Math.max(2, mediumHeaderMinHeightPx - 8);
                            const collapsedHeaderFontRem = Math.max(0.44, Number((mediumHeaderFontRem - 0.18).toFixed(2)));

                            const headerPadPx = isCollapsedMode
                                ? collapsedHeaderPadPx
                                : isMediumMode
                                    ? mediumHeaderPadPx
                                    : normalHeaderPadPx;
                            const headerMinHeightPx = isCollapsedMode
                                ? collapsedHeaderMinHeightPx
                                : isMediumMode
                                    ? mediumHeaderMinHeightPx
                                    : normalHeaderMinHeightPx;
                            const headerFontSize = isCollapsedMode
                                ? `${collapsedHeaderFontRem}rem`
                                : isMediumMode
                                    ? `${mediumHeaderFontRem}rem`
                                    : `${normalHeaderFontRem}rem`;
                            const headerLeftPad = isCollapsedMode
                                ? "0.03rem"
                                : isMediumMode
                                    ? "0.08rem"
                                    : "0.1rem";
                            const headerLineHeight = isCollapsedMode ? 1 : isMediumMode ? 1.12 : 1.2;
                            const headerDisplayText = isCollapsedMode ? "" : `${card.headerText || ""}`;
                            const extraMargin = Number(cardState.ExtraMargin || 0);
                            //const extraMarginForCalc = minOpenTicketCount <= 2 ? (extraMargin <= 6 ? 0.90 : 0) : 0;
                            const isLastCard = index === displayCardsData.length - 1;
                            const marginBottomPx = isLastCard ? "0px" : `${4.2 + extraMargin}px`;

                            return (
                                <div
                                    className="card"
                                    key={card.id}
                                    ref={(el) => (cardRefs.current[index] = el)}
                                    style={{
                                        //backgroundColor:  themeStyles.cardBackground,
                                        //backgroundColor: index % 2 !== 0 ? (isDarkTheme ? "#333333" : "#f6f6f6") : "transparent",
                                        backgroundColor: index % 2 !== 0 ? (isDarkTheme ? "#333333" : "transparent") : "transparent",
                                        border: isCollapsedMode || isMediumMode ? "none" : themeStyles.cardBorder,
                                        //border: isCollapsedMode || isMediumMode ? "none" : `1px solid ${colors[index % colors.length]}`,
                                        color: themeStyles.bodyTextColor,
                                        borderRadius: "5px",
                                        opacity: isReady ? 1 : 0,
                                        transform: cardTransform,
                                        transition: "opacity 500ms cubic-bezier(0.22, 1, 0.36, 1), transform 500ms cubic-bezier(0.22, 1, 0.36, 1)",
                                        transitionDelay: `${Math.min(index, 8) * 36}ms`,
                                        marginBottom: marginBottomPx
                                    }}
                                    onMouseEnter={(event) => {
                                        if (!isReady) return;
                                        event.currentTarget.style.transform = "translateY(-1px)";
                                    }}
                                    onMouseLeave={(event) => {
                                        if (!isReady) return;
                                        event.currentTarget.style.transform = "translateY(0px)";
                                    }}
                                >
                                    <div
                                        className="card-header fw-bold"
                                        style={{
                                            cursor: "pointer",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            paddingTop: `${headerPadPx}px`,
                                            paddingBottom: `${headerPadPx}px`,
                                            minHeight: `${headerMinHeightPx}px`,
                                            fontSize: headerFontSize,
                                            lineHeight: headerLineHeight,
                                            backgroundColor: colors[index % colors.length],
                                            border: `1px solid ${colors[index % colors.length]}`,
                                            color: compactStatusStyle.showHeaderText ? "#ffffff" : "transparent",
                                            borderTopLeftRadius: isCollapsedMode || isMediumMode ? "0" : "4px",
                                            borderTopRightRadius: isCollapsedMode || isMediumMode ? "0" : "4px",
                                            display: "flex",
                                            alignItems: "center",
                                            textAlign: "left",
                                            paddingLeft: headerLeftPad,
                                            transition: "color 180ms ease",
                                        }}
                                        title={headerDisplayText}
                                        onClick={() => toggleCard(index)}
                                    >
                                        {headerDisplayText}
                                    </div>

                                    <div
                                        className="card-body collapse show"
                                        style={{
                                            //backgroundColor: themeStyles.cardBackground,
                                            color: themeStyles.bodyTextColor,
                                            overflow: "hidden",
                                            transition: disableBodyTransition ? "none" : "height 560ms cubic-bezier(0.22, 1, 0.36, 1), opacity 460ms cubic-bezier(0.22, 1, 0.36, 1)",
                                            height: bodyHeight,
                                            maxHeight: bodyHeight,
                                            display: bodyDisplay,
                                            flexDirection: renderMode === "open" ? "column" : undefined,
                                            justifyContent: bodyJustifyContent,
                                            gap: renderMode === "open" ? "4px" : "0",
                                            padding: bodyPadding,
                                            margin: bodyMargin,
                                            opacity: isBodyOpen ? 1 : 0,
                                            pointerEvents: isBodyOpen ? "auto" : "none",
                                            borderRadius: "0",
                                        }}
                                    >
                                        <div
                                            className="row align-items-center"
                                            style={{
                                                flex: "0 0 auto",
                                                opacity: contentOpacity,
                                                transition: contentTransition,
                                                transitionDelay: disableBodyTransition ? "0ms" : "120ms",
                                            }}
                                        >
                                            <div className="col text-start">
                                                <div
                                                    style={{
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        maxWidth: "120px",
                                                        fontSize: `${compactStatusStyle.topLeftFontRem}rem`,
                                                    }}
                                                    title={card.topLeftText}
                                                >
                                                    {card.topLeftText}
                                                </div>
                                            </div>
                                            <div
                                                className="col text-center"
                                                style={{
                                                    paddingTop: `${compactStatusStyle.statusPadPx}px`,
                                                    paddingBottom: `${Math.max(1, Math.floor(compactStatusStyle.statusPadPx / 2))}px`,
                                                }}
                                            >
                                                <div style={{ fontSize: `${compactStatusStyle.statusLabelRem}rem`, lineHeight: 1 }}>Status</div>
                                                <div className="fw-bold" style={{ fontSize: `${compactStatusStyle.statusValueRem}rem`, lineHeight: 1.1 }}>{card.percent}</div>
                                            </div>
                                            <div className="col d-flex flex-column align-items-end">
                                                <div style={{ width: `${compactStatusStyle.avatarWrapWidthPx}px`, display: "flex", justifyContent: "center" }}>
                                                    {!card.imageUrl || imageErrors[card.id] ? (
                                                        <div
                                                            style={{
                                                                width: compactStatusStyle.avatarSizePx,
                                                                height: compactStatusStyle.avatarSizePx,
                                                                borderRadius: "50%",
                                                                backgroundColor: colors[0] || "#5f8b95",
                                                                color: isDarkTheme ? "#ffffff" : "white",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                fontSize: `${compactStatusStyle.avatarFontPx}px`,
                                                                fontWeight: "bold",
                                                            }}
                                                        >
                                                            {getInitials(card.owner)}
                                                        </div>
                                                    ) : (
                                                        <img
                                                            src={card.imageUrl}
                                                            alt="avatar"
                                                            style={{
                                                                width: compactStatusStyle.avatarSizePx,
                                                                height: compactStatusStyle.avatarSizePx,
                                                                borderRadius: "50%",
                                                            }}
                                                            onError={() => handleImageError(card.id)}
                                                        />
                                                    )}
                                                </div>

                                                <div
                                                    style={{
                                                        width: `${compactStatusStyle.avatarWrapWidthPx}px`,
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        textAlign: "center",
                                                        color: themeStyles.bodyTextColor,
                                                        fontSize: `${compactStatusStyle.ownerFontRem}rem`,
                                                    }}
                                                    title={card.owner}
                                                >
                                                    {formatOwnerName(card.owner)}
                                                </div>
                                            </div>
                                        </div>

                                        <div
                                            style={{
                                                flex: "1 1 auto",
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "center",
                                                paddingTop: "2px",
                                                paddingBottom: "2px",
                                                minHeight: "52px",
                                            }}
                                        >
                                            <p
                                                className="mt-2 fw-bold text-center"
                                                style={{
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: compactStatusStyle.titleLines,
                                                    WebkitBoxOrient: "vertical",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    fontSize: `${compactStatusStyle.titleFontRem}rem`,
                                                    lineHeight: 1.2,
                                                    marginTop: "0",
                                                    marginBottom: "0.25rem",
                                                    opacity: contentOpacity,
                                                    transition: contentTransition,
                                                    transitionDelay: disableBodyTransition ? "0ms" : "210ms",
                                                }}
                                                title={card.title}
                                            >
                                                {card.title}
                                            </p>
                                            <p
                                                className="mt-1 text-center"
                                                style={{
                                                    color: themeStyles.mutedTextColor,
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: compactStatusStyle.descLines,
                                                    WebkitBoxOrient: "vertical",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    fontSize: `${compactStatusStyle.descFontRem}rem`,
                                                    lineHeight: 1.2,
                                                    marginTop: "0",
                                                    marginBottom: "0",
                                                    opacity: contentOpacity,
                                                    transition: contentTransition,
                                                    transitionDelay: disableBodyTransition ? "0ms" : "290ms",
                                                }}
                                                title={card.description}
                                            >
                                                {card.description}
                                            </p>
                                        </div>

                                        <div
                                            className="row small"
                                            style={{
                                                flex: "0 0 auto",
                                                alignItems: "center",
                                                fontSize: `${compactStatusStyle.metaFontRem}rem`,
                                                lineHeight: 1.2,
                                                opacity: contentOpacity,
                                                transition: contentTransition,
                                                transitionDelay: disableBodyTransition ? "0ms" : "360ms",
                                            }}
                                        >
                                            <div className="col">
                                                <b>Opened</b>
                                                <br />
                                                {card.openDate}
                                            </div>
                                            <div className="col text-center">
                                                <b>Promised</b>
                                                <br />
                                                {card.promiseDate}
                                            </div>
                                            <div className="col d-flex flex-column align-items-end">
                                                <div style={{
                                                    width: "80px",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                    textAlign: "center",
                                                }}>
                                                    <b>{card.status}</b>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()
                    ))}
                </div>
            </div>
        </div>
    );
}
